// MyPDRs.jsx
import { useState } from 'react'
import { patch, del } from '../lib/api'

const GOAL_THEMES = [
  { color:'#4F46E5', bg:'#EEF2FF', border:'#C7D2FE' },
  { color:'#059669', bg:'#ECFDF5', border:'#A7F3D0' },
  { color:'#D97706', bg:'#FFFBEB', border:'#FDE68A' },
  { color:'#7C3AED', bg:'#F5F3FF', border:'#DDD6FE' },
  { color:'#DB2777', bg:'#FDF2F8', border:'#FBCFE8' },
  { color:'#0EA5E9', bg:'#F0F9FF', border:'#BAE6FD' },
  { color:'#EF4444', bg:'#FEF2F2', border:'#FECACA' },
  { color:'#F97316', bg:'#FFF7ED', border:'#FED7AA' },
]
function gt(i) { return GOAL_THEMES[i % GOAL_THEMES.length] }
function progressColor(p) { return p >= 90 ? '#059669' : p >= 60 ? '#4F46E5' : p >= 30 ? '#D97706' : '#EF4444' }

function DonutChart({ progress, size = 64 }) {
  const r   = size / 2 - 7
  const c   = 2 * Math.PI * r
  const off = c - (Math.min(progress, 100) / 100) * c
  const col = progressColor(progress)
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={6}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize={12} fontWeight="800"
        fill={col} fontFamily="Inter,sans-serif">{Math.round(progress)}%</text>
    </svg>
  )
}

const STATUS = {
  draft:            { bg: '#F1F5F9', color: '#64748B', label: 'Draft',            icon: '💾' },
  pending_approval: { bg: '#FFFBEB', color: '#D97706', label: 'Pending Approval', icon: '⏳' },
  active:           { bg: '#ECFDF5', color: '#059669', label: 'Active',            icon: '🟢' },
  rejected:         { bg: '#FEF2F2', color: '#DC2626', label: 'Rejected',          icon: '❌' },
  review_submitted: { bg: '#EFF6FF', color: '#4F46E5', label: 'Under Review',      icon: '🔍' },
  completed:        { bg: '#F5F3FF', color: '#7C3AED', label: 'Completed',         icon: '✅' },
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN — list of PDR cards
// ════════════════════════════════════════════════════════════════════════════
export default function MyPDRs({ t, pdrs, navigate, onRefresh, setNav, setReviewPreselect }) {
  const [selectedPdr, setSelectedPdr] = useState(null)
  const [deletingId, setDeletingId]   = useState(null)
  const [deleting, setDeleting]       = useState(false)

  async function handleDelete(pdrId) {
    setDeleting(true)
    try {
      await del(`/pdrs/${pdrId}`) // server also removes the PDR's goals/updates/evidences
    } catch (e) { console.error(e.message) }
    setDeletingId(null)
    setDeleting(false)
    setSelectedPdr(null)
    onRefresh()
  }

  if (selectedPdr) {
    // Find the fresh version from pdrs array
    const fresh = pdrs.find(p => p.id === selectedPdr.id) || selectedPdr
    return (
      <PDRDetail
        t={t} pdr={fresh} pdrs={pdrs}
        onBack={() => setSelectedPdr(null)}
        onRefresh={onRefresh}
        navigate={navigate}
        setNav={setNav}
        setReviewPreselect={setReviewPreselect}
        deletingId={deletingId}
        setDeletingId={setDeletingId}
        deleting={deleting}
        onDelete={handleDelete}
      />
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.75rem' }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>My PDR Cycles</h1>
          <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>{pdrs.length} performance development review{pdrs.length!==1?'s':''}</p>
        </div>
        <button onClick={() => navigate('/pdr/new')}
          style={{ padding:'10px 20px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', boxShadow:'0 2px 8px rgba(79,70,229,0.3)' }}>
          + New PDR Cycle
        </button>
      </div>

      {pdrs.length === 0 ? (
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'4rem 2rem', textAlign:'center', boxShadow:t.shadow }}>
          <div style={{ fontSize:52, marginBottom:'1rem' }}>📋</div>
          <h3 style={{ fontSize:20, fontWeight:700, color:t.text, marginBottom:8 }}>No PDR cycles yet</h3>
          <p style={{ fontSize:14, color:t.textMuted, marginBottom:'1.5rem' }}>Create your first PDR cycle to start tracking your goals.</p>
          <button onClick={() => navigate('/pdr/new')}
            style={{ padding:'12px 28px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
            Create first PDR →
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {pdrs.map((pdr, idx) => {
            const s       = STATUS[pdr.status] || STATUS.draft
            const goals   = (pdr.pdr_goals || []).sort((a,b) => a.goal_number - b.goal_number)
            const overall = Math.round(goals.length
              ? goals.reduce((sum,g) => sum + ((g.progress||0)*((g.weightage||0)/100)), 0)
              : 0)
            const isDraft     = pdr.status === 'draft'
            const isRejected  = pdr.status === 'rejected'
            const isCompleted = pdr.status === 'completed'
            const isLocked    = isCompleted || pdr.status === 'review_submitted'
            const isDeleting  = deletingId === pdr.id

            return (
              <div key={pdr.id} style={{ background:t.card, border:`1px solid ${isRejected?'#FECACA':isDraft?'#E2E8F0':isCompleted?'#A7F3D0':t.cardBorder}`, borderRadius:14, overflow:'hidden', boxShadow:t.shadow }}>

                {/* Delete confirm */}
                {isDeleting && (
                  <div style={{ background:'#FEF2F2', borderBottom:'1px solid #FECACA', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'#DC2626' }}>⚠️ Delete this PDR and all its goals permanently?</span>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => handleDelete(pdr.id)} disabled={deleting}
                        style={{ padding:'6px 14px', background:'#EF4444', border:'none', borderRadius:7, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer' }}>
                        {deleting ? 'Deleting…' : 'Yes, delete'}
                      </button>
                      <button onClick={() => setDeletingId(null)}
                        style={{ padding:'6px 14px', background:'transparent', border:'1px solid #FECACA', borderRadius:7, fontSize:12, fontWeight:600, color:'#DC2626', cursor:'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Card row */}
                <div style={{ padding:'1.25rem 1.5rem', display:'flex', alignItems:'center', gap:16 }}>

                  {/* PDR number badge */}
                  <div style={{ width:40, height:40, borderRadius:10, background:`linear-gradient(135deg,#4F46E5,#6366F1)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
                    {idx + 1}
                  </div>

                  {/* Donut */}
                  <DonutChart progress={overall} size={64} />

                  {/* Info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:16, fontWeight:700, color:t.text, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {pdr.title}
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontSize:11, fontWeight:600, background:`${t.primary}14`, color:t.primary, padding:'2px 8px', borderRadius:99, textTransform:'capitalize' }}>
                        {pdr.review_type}
                      </span>
                      <span style={{ fontSize:11, color:t.textMuted }}>📅 {pdr.start_date} → {pdr.end_date}</span>
                      <span style={{ fontSize:11, color:t.textMuted }}>{goals.length} goal{goals.length!==1?'s':''}</span>
                    </div>
                    {isRejected && pdr.rejection_reason && (
                      <div style={{ fontSize:12, color:'#DC2626', marginTop:4, fontWeight:600 }}>
                        ❌ Rejected: {pdr.rejection_reason.slice(0, 80)}{pdr.rejection_reason.length>80?'…':''}
                      </div>
                    )}
                  </div>

                  {/* Status + actions */}
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, flexShrink:0 }}>
                    <span style={{ fontSize:11, fontWeight:600, background:s.bg, color:s.color, padding:'4px 10px', borderRadius:99, border:`1px solid ${s.color}30` }}>
                      {s.icon} {s.label}
                    </span>
                    <div style={{ display:'flex', gap:6 }}>
                      {!isLocked && isDraft && (
                        <button onClick={() => navigate('/pdr/new', { state:{ draft: pdr } })}
                          style={{ padding:'6px 12px', background:'#4F46E514', border:'1px solid #4F46E530', borderRadius:7, fontSize:12, fontWeight:600, color:'#4F46E5', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                          ✏️ Edit
                        </button>
                      )}
                      {!isLocked && isRejected && (
                        <button onClick={() => navigate('/pdr/new', { state:{ draft: pdr } })}
                          style={{ padding:'6px 12px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:7, fontSize:12, fontWeight:600, color:'#DC2626', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                          ✏️ Edit & Resubmit
                        </button>
                      )}
                      {isCompleted && (
                        <span style={{ fontSize:11, fontWeight:700, background:'#ECFDF5', color:'#059669', padding:'4px 10px', borderRadius:99, border:'1px solid #A7F3D0' }}>
                          🔒 Cycle closed
                        </span>
                      )}
                      <button onClick={() => setSelectedPdr(pdr)}
                        style={{ padding:'6px 14px', background:`${t.primary}14`, border:`1px solid ${t.primary}30`, borderRadius:7, fontSize:12, fontWeight:600, color:t.primary, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                        View →
                      </button>
                      {!isCompleted && (
                        <button onClick={() => setDeletingId(pdr.id)}
                          style={{ padding:'6px 10px', background:'transparent', border:'1px solid #FECACA', borderRadius:7, fontSize:12, color:'#EF4444', cursor:'pointer' }}>
                          🗑
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// PDR DETAIL — full view of one PDR
// ════════════════════════════════════════════════════════════════════════════
function PDRDetail({ t, pdr, onBack, onRefresh, navigate, setNav, setReviewPreselect, deletingId, setDeletingId, deleting, onDelete }) {
  const [updatingPdr, setUpdatingPdr] = useState(null)

  const s       = STATUS[pdr.status] || STATUS.draft
  const goals   = (pdr.pdr_goals || []).sort((a,b) => a.goal_number - b.goal_number)
  const overall = Math.round(goals.length
    ? goals.reduce((sum,g) => sum + ((g.progress||0)*((g.weightage||0)/100)), 0)
    : 0)
  const isActive    = pdr.status === 'active'
  const isDraft     = pdr.status === 'draft'
  const isRejected  = pdr.status === 'rejected'
  const isCompleted = pdr.status === 'completed'
  const isLocked    = isCompleted || pdr.status === 'review_submitted'
  const isDeleting  = deletingId === pdr.id

  return (
    <div>
      {/* Back */}
      <button onClick={onBack}
        style={{ fontSize:13, color:t.textMuted, background:'none', border:'none', cursor:'pointer', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:6, fontFamily:'Inter,system-ui' }}>
        ← Back to PDRs
      </button>

      {/* Delete confirm */}
      {isDeleting && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'14px 16px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#DC2626' }}>⚠️ Delete this PDR and all its goals permanently?</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => onDelete(pdr.id)} disabled={deleting}
              style={{ padding:'7px 16px', background:'#EF4444', border:'none', borderRadius:8, fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer' }}>
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </button>
            <button onClick={() => setDeletingId(null)}
              style={{ padding:'7px 16px', background:'transparent', border:'1px solid #FECACA', borderRadius:8, fontSize:13, fontWeight:600, color:'#DC2626', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* PDR header card */}
      <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow }}>
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:'1rem' }}>
          <DonutChart progress={overall} size={80} />
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:20, fontWeight:800, color:t.text, margin:'0 0 6px', letterSpacing:'-0.3px' }}>{pdr.title}</h2>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:12, fontWeight:600, background:`${t.primary}14`, color:t.primary, padding:'3px 10px', borderRadius:99, textTransform:'capitalize' }}>{pdr.review_type}</span>
              <span style={{ fontSize:12, color:t.textMuted }}>📅 {pdr.start_date} → {pdr.end_date}</span>
              <span style={{ fontSize:12, color:t.textMuted }}>{goals.length} goals</span>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
            <span style={{ fontSize:12, fontWeight:600, background:s.bg, color:s.color, padding:'5px 14px', borderRadius:99, border:`1px solid ${s.color}30` }}>
              {s.icon} {s.label}
            </span>
            {!isCompleted && (
              <button onClick={() => setDeletingId(pdr.id)}
                style={{ padding:'6px 14px', background:'transparent', border:'1px solid #FECACA', borderRadius:8, fontSize:12, fontWeight:600, color:'#EF4444', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                🗑 Delete PDR
              </button>
            )}
          </div>
        </div>

        {/* Rejection reason */}
        {isRejected && pdr.rejection_reason && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#DC2626', lineHeight:1.6 }}>
            <strong>❌ Rejected:</strong> {pdr.rejection_reason}
            <div style={{ fontSize:12, color:'#B91C1C', marginTop:6 }}>Please delete this PDR and resubmit with the requested changes.</div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop: isRejected ? 12 : 0, paddingTop: isRejected || isDraft ? 12 : 0, borderTop: isRejected || isDraft ? `1px solid ${t.border}` : 'none' }}>
          {isDraft && (
            <button onClick={() => navigate('/pdr/new', { state:{ draft: pdr } })}
              style={{ padding:'9px 18px', background:'#4F46E514', border:'1px solid #4F46E530', borderRadius:8, fontSize:13, fontWeight:600, color:'#4F46E5', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              ✏️ Continue Editing Draft
            </button>
          )}
          {isRejected && (
            <button onClick={() => navigate('/pdr/new', { state:{ draft: pdr } })}
              style={{ padding:'9px 20px', background:'linear-gradient(135deg,#EF4444,#DC2626)', border:'none', borderRadius:8, fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', boxShadow:'0 2px 8px rgba(239,68,68,0.3)', display:'flex', alignItems:'center', gap:6 }}>
              ✏️ Edit & Resubmit
            </button>
          )}
          {isCompleted && (
            <div style={{ background:'#ECFDF5', border:'1px solid #A7F3D0', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:12, width:'100%' }}>
              <span style={{ fontSize:24 }}>🏆</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:'#059669' }}>PDR Cycle Complete</div>
                <div style={{ fontSize:12, color:'#047857' }}>This PDR has been reviewed and signed off by both parties. No further changes are allowed.</div>
              </div>
            </div>
          )}
          {!isCompleted && isActive && (
            <>
              <button onClick={() => setUpdatingPdr(pdr)}
                style={{ padding:'9px 18px', background:'#10B98114', border:'1px solid #10B98130', borderRadius:8, fontSize:13, fontWeight:600, color:'#10B981', cursor:'pointer', fontFamily:'Inter,system-ui', display:'flex', alignItems:'center', gap:6 }}>
                📊 Update Progress
              </button>
              <button onClick={() => { if (setReviewPreselect) setReviewPreselect(pdr.id); if (setNav) setNav('reviews') }}
                style={{ padding:'9px 18px', background:'#4F46E514', border:'1px solid #4F46E530', borderRadius:8, fontSize:13, fontWeight:600, color:'#4F46E5', cursor:'pointer', fontFamily:'Inter,system-ui', display:'flex', alignItems:'center', gap:6 }}>
                ⭐ Submit for Review →
              </button>
            </>
          )}
          {!isCompleted && pdr.status === 'pending_approval' && (
            <div style={{ fontSize:13, color:'#D97706', background:'#FFFBEB', padding:'8px 14px', borderRadius:8, border:'1px solid #FDE68A', display:'flex', alignItems:'center', gap:6 }}>
              ⏳ Waiting for manager approval
            </div>
          )}
        </div>
      </div>

      {/* Goals */}
      <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
          <div style={{ fontSize:14, fontWeight:700, color:t.text }}>Goal Progress</div>
          <div style={{ fontSize:13, fontWeight:800, color:'#4F46E5' }}>Overall: {overall}%</div>
        </div>

        {goals.length === 0 ? (
          <p style={{ fontSize:13, color:t.textMuted }}>No goals attached to this PDR.</p>
        ) : (
          <>
            {goals.map((g, i) => {
              const prog  = g.progress || 0
              const theme = gt(i)
              const sc = prog >= 100 ? { bg:'#ECFDF5', color:'#059669', label:'✅ Completed' }
                       : prog >= 60  ? { bg:'#EFF6FF', color:'#4F46E5', label:'🎯 On Track' }
                       : prog >= 30  ? { bg:'#FFFBEB', color:'#D97706', label:'⚠️ At Risk' }
                       :               { bg:'#F1F5F9', color:'#64748B', label:'⏳ Not Started' }
              return (
                <div key={g.id} style={{ marginBottom:12, border:`1px solid ${theme.border}`, borderLeft:`5px solid ${theme.color}`, borderRadius:12, overflow:'hidden' }}>
                  <div style={{ background:theme.bg, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:26, height:26, borderRadius:'50%', background:theme.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900, color:'#fff', flexShrink:0 }}>{i+1}</div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#1E293B' }}>{g.title}</div>
                        {g.kpi && <div style={{ fontSize:11, color:'#64748B', marginTop:1 }}>KPI: {g.kpi}</div>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
                      <span style={{ fontSize:11, fontWeight:700, background:`${theme.color}20`, color:theme.color, padding:'2px 9px', borderRadius:99 }}>{g.weightage}% weight</span>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:99, background:sc.bg, color:sc.color }}>{sc.label}</span>
                    </div>
                  </div>
                  <div style={{ padding:'10px 14px', background:'#fff' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <span style={{ fontSize:12, color:'#64748B' }}>Progress</span>
                      <span style={{ fontSize:16, fontWeight:900, color:progressColor(prog) }}>{prog}%</span>
                    </div>
                    <div style={{ background:'#E5E7EB', borderRadius:99, height:10, overflow:'hidden' }}>
                      <div style={{ width:`${prog}%`, height:'100%', background:`linear-gradient(90deg,${theme.color},${theme.color}cc)`, borderRadius:99, transition:'width .5s' }} />
                    </div>
                    {g.progress_notes && (
                      <div style={{ fontSize:12, color:'#475569', marginTop:8, background:'#F8F9FA', borderRadius:8, padding:'6px 10px', fontStyle:'italic', display:'flex', gap:6 }}>
                        <span>💬</span><span>{g.progress_notes}</span>
                      </div>
                    )}
                    {g.last_updated_at && (
                      <div style={{ fontSize:10, color:'#9CA3AF', marginTop:4 }}>
                        Updated {new Date(g.last_updated_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Overall weighted bar */}
            <div style={{ background:t.dark?'rgba(99,102,241,0.1)':'#F0F0FF', borderRadius:10, padding:'10px 14px', marginTop:4 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:12, fontWeight:700, color:t.text }}>Overall weighted progress</span>
                <span style={{ fontSize:14, fontWeight:900, color:'#4F46E5' }}>{overall}%</span>
              </div>
              <div style={{ background:t.border, borderRadius:99, height:10 }}>
                <div style={{ width:`${overall}%`, height:'100%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', borderRadius:99, transition:'width .5s' }} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Update Progress Modal */}
      {updatingPdr && (
        <UpdateProgressModal
          t={t} pdr={updatingPdr}
          onClose={() => setUpdatingPdr(null)}
          onSave={() => { setUpdatingPdr(null); onRefresh() }}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// UPDATE PROGRESS MODAL
// ════════════════════════════════════════════════════════════════════════════
function UpdateProgressModal({ t, pdr, onClose, onSave }) {
  const goals = (pdr.pdr_goals || []).sort((a,b) => a.goal_number - b.goal_number)
  const [entries, setEntries] = useState(
    goals.map(g => ({
      id: g.id, title: g.title, weightage: g.weightage,
      goal_number: g.goal_number, progress: g.progress || 0, notes: g.progress_notes || '',
    }))
  )
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  function update(i, field, value) {
    const upd = [...entries]; upd[i] = { ...upd[i], [field]: value }; setEntries(upd)
  }

  async function handleSave() {
    setSaving(true); setError('')
    try {
      for (const e of entries) {
        await patch(`/pdrs/goals/${e.id}`, {
          progress:        parseInt(e.progress),
          progress_notes:  e.notes,
          status:          e.progress >= 100 ? 'completed' : e.progress > 0 ? 'in-progress' : 'not-started',
          last_updated_at: new Date().toISOString(),
        })
      }
    } catch (err) { setError(err.message); setSaving(false); return }
    setSaving(false); onSave()
  }

  const IS = { display:'block', width:'100%', padding:'10px 14px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none' }
  const overallWeighted = Math.round(entries.reduce((s,e) => s + (parseInt(e.progress)||0)*(e.weightage/100), 0))

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ background:t.card, borderRadius:16, width:'100%', maxWidth:640, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

        <div style={{ padding:'1.25rem 1.5rem', borderBottom:`1px solid ${t.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:t.dark?'rgba(99,102,241,0.08)':'#F8F9FF' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:t.text }}>📊 Update Progress</div>
            <div style={{ fontSize:13, color:t.textMuted, marginTop:2 }}>{pdr.title}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:10, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.06em' }}>Overall</div>
              <div style={{ fontSize:20, fontWeight:900, color:progressColor(overallWeighted) }}>{overallWeighted}%</div>
            </div>
            <button onClick={onClose}
              style={{ width:32, height:32, background:'none', border:`1px solid ${t.border}`, borderRadius:8, fontSize:18, cursor:'pointer', color:t.textMuted, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          </div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem' }}>
          {entries.map((e, i) => {
            const theme = gt(i)
            const prog  = parseInt(e.progress) || 0
            return (
              <div key={e.id} style={{ marginBottom:20, borderLeft:`4px solid ${theme.color}`, paddingLeft:14, paddingBottom:16, borderBottom:i<entries.length-1?`1px solid ${t.border}`:'none' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:24, height:24, borderRadius:'50%', background:theme.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900, color:'#fff', flexShrink:0 }}>{e.goal_number}</div>
                    <span style={{ fontSize:14, fontWeight:700, color:t.text }}>{e.title}</span>
                    <span style={{ fontSize:11, color:t.textMuted }}>({e.weightage}%)</span>
                  </div>
                  <span style={{ fontSize:16, fontWeight:900, color:progressColor(prog) }}>{prog}%</span>
                </div>

                <div style={{ background:t.border, borderRadius:99, height:8, marginBottom:12, overflow:'hidden' }}>
                  <div style={{ width:`${prog}%`, height:'100%', background:theme.color, borderRadius:99, transition:'width .2s' }} />
                </div>

                <div style={{ marginBottom:10 }}>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:t.textMuted, marginBottom:6 }}>
                    Progress: <span style={{ color:theme.color, fontWeight:800 }}>{prog}%</span>
                  </label>
                  <input type="range" min={0} max={100} step={5}
                    value={e.progress}
                    onChange={ev => update(i,'progress',ev.target.value)}
                    style={{ width:'100%', accentColor:theme.color, cursor:'pointer' }} />
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:t.textMuted, marginTop:2 }}>
                    <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                  </div>
                </div>

                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:t.textMuted, marginBottom:6 }}>Progress notes</label>
                  <textarea value={e.notes} onChange={ev => update(i,'notes',ev.target.value)}
                    placeholder="What have you done so far? Any blockers?"
                    rows={2} style={{ ...IS, resize:'vertical', fontSize:13 }} />
                </div>
              </div>
            )
          })}
        </div>

        <div style={{ padding:'1rem 1.5rem', borderTop:`1px solid ${t.border}`, background:t.card }}>
          {error && <div style={{ fontSize:13, color:'#EF4444', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'8px 12px', marginBottom:10 }}>⚠️ {error}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={onClose}
              style={{ padding:'10px 20px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:8, fontSize:14, fontWeight:500, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'10px 24px', background:saving?t.border:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:700, color:saving?t.textMuted:'#fff', cursor:saving?'not-allowed':'pointer', fontFamily:'Inter,system-ui' }}>
              {saving ? 'Saving…' : '✅ Save Progress'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
